import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, LifeBuoy, Plus, RefreshCw, Search } from 'lucide-react';
import { TICKET_CATEGORY_LABEL, TICKET_OPEN, TICKET_PRIORITY_LABEL, formatDateTime } from '@nu/shared';
import { api } from '../../api/client.js';
import Modal from '../../components/Modal.jsx';
import { outbox } from '../../offline/index.js';
import { problem } from '../bookings/util.js';
import { useMyTickets } from './store.js';
import TicketThread, { TicketStatus } from './TicketThread.jsx';

// Helpdesk: /helpdesk and /helpdesk/:id. Everyone sees the tickets they
// raised; the helpdesk team (helpdesk.work) also gets the queue. In the
// office a ticket opens in a pop-up over the list; in the field it's a page.
export default function Helpdesk({ me, shell }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const agent = me.capabilities.includes('helpdesk.work');

  if (shell === 'field') {
    if (id) {
      return (
        <section className="card">
          <button type="button" className="link-btn back-link" onClick={() => navigate('/support')}>
            <ArrowLeft size={18} aria-hidden="true" /> Support
          </button>
          <TicketThread id={id} me={me} agent={agent} />
        </section>
      );
    }
    return (
      <section className="card">
        <button type="button" className="link-btn back-link" onClick={() => navigate('/support')}>
          <ArrowLeft size={18} aria-hidden="true" /> Support
        </button>
        <div className="page-head">
          <h1>My tickets</h1>
          <Link to="/helpdesk/new" className="btn primary with-icon"><Plus size={18} aria-hidden="true" /> Log a ticket</Link>
        </div>
        <MyTickets />
      </section>
    );
  }

  return <OfficeHelpdesk me={me} agent={agent} openId={id} />;
}

function OfficeHelpdesk({ me, agent, openId }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(agent ? 'queue' : 'mine');
  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Helpdesk</h1>
            <p className="muted">Problems with the platform or the app, access requests and anything else IT. {agent ? 'You’re on the helpdesk: every ticket is in the queue.' : 'You’ll get a notification and an email when the helpdesk replies.'}</p>
          </div>
          <Link to="/helpdesk/new" className="btn primary with-icon"><Plus size={18} aria-hidden="true" /> Log a ticket</Link>
        </div>
        {agent && (
          <div className="view-toggle ticket-tabs" role="tablist" aria-label="Helpdesk">
            <button type="button" role="tab" aria-selected={tab === 'queue'} className={`btn${tab === 'queue' ? ' active' : ''}`} onClick={() => setTab('queue')}>Queue</button>
            <button type="button" role="tab" aria-selected={tab === 'mine'} className={`btn${tab === 'mine' ? ' active' : ''}`} onClick={() => setTab('mine')}>My tickets</button>
          </div>
        )}
      </section>
      <section className="card">
        {tab === 'queue' ? <Queue me={me} /> : <MyTickets />}
      </section>
      <Modal open={Boolean(openId)} title="Ticket" wide onClose={() => navigate('/helpdesk')}>
        {openId && <TicketThread id={openId} me={me} agent={agent} />}
      </Modal>
    </div>
  );
}

// Tickets I raised (also on the field Support screen, `limit` rows).
export function MyTickets({ limit = 0 }) {
  const { tickets, status } = useMyTickets();
  if (!tickets) return <p className="muted">{status === 'error' ? 'Your tickets can’t be loaded with no signal.' : 'Loading…'}</p>;
  if (!tickets.length) {
    return (
      <div className="empty">
        <LifeBuoy size={36} aria-hidden="true" />
        <p className="muted">No tickets yet. Log one when something isn&apos;t working or you need access to something.</p>
      </div>
    );
  }
  const shown = limit ? tickets.slice(0, limit) : tickets;
  return (
    <>
      <ul className="msg-list">
        {shown.map((t) => {
          const replied = t.lastSide === 'agent' && TICKET_OPEN.includes(t.status);
          return (
            <li key={t.id}>
              <Link to={`/helpdesk/${t.id}`} className={`msg-row${replied ? ' unread' : ''}`}>
                <span className="msg-dot" aria-hidden="true" />
                <span className="msg-row-main">
                  <span className="msg-row-top">
                    <span className="msg-dept">{t.reference || 'Sending…'}</span>
                    <span className="small muted">{formatDateTime(t.lastMessageAt)}</span>
                  </span>
                  <strong className="msg-subject">{t.subject}</strong>
                  <span className="msg-tags">
                    <TicketStatus status={t.status} raiser />
                    {replied && <span className="status status-on_the_way">Helpdesk replied</span>}
                  </span>
                </span>
                <ChevronRight size={20} aria-hidden="true" className="muted" />
              </Link>
            </li>
          );
        })}
      </ul>
      {limit > 0 && tickets.length > limit && <p className="small"><Link to="/helpdesk">All my tickets ({tickets.length})</Link></p>}
    </>
  );
}

const VIEWS = [
  { id: 'needs_reply', label: 'Needs reply' },
  { id: 'open', label: 'Open' },
  { id: 'unassigned', label: 'Unassigned' },
  { id: 'mine', label: 'Assigned to me' },
  { id: 'waiting', label: 'Waiting on user' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'all', label: 'All' },
];

function Queue({ me }) {
  const navigate = useNavigate();
  const [view, setView] = useState('open');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const load = useCallback(() => api(`/helpdesk/queue?view=${view}`).then((d) => { setData(d); setError(null); }, (e) => setError(problem(e))), [view]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 30_000);
    const off = outbox.subscribe(load);
    return () => { clearInterval(t); off(); };
  }, [load]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data?.tickets || []).filter((t) => !s || `${t.reference} ${t.subject} ${t.raisedByName} ${t.raisedByEmail} ${t.assignedToName || ''}`.toLowerCase().includes(s));
  }, [data, q]);

  return (
    <>
      <div className="ticket-views" role="tablist" aria-label="Queue">
        {VIEWS.map((v) => (
          <button key={v.id} type="button" role="tab" aria-selected={view === v.id} className={`chip${view === v.id ? ' on' : ''}`} onClick={() => setView(v.id)}>
            {v.label}{data ? ` (${data.counts[v.id] ?? 0})` : ''}
          </button>
        ))}
        <button type="button" className="btn icon-only" aria-label="Refresh" onClick={load}><RefreshCw size={18} aria-hidden="true" /></button>
      </div>
      <label className="search">
        <Search size={18} aria-hidden="true" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, title, who raised it" aria-label="Search tickets" />
      </label>
      {error && <p className="notice-line notice-bad">{error}</p>}
      {!data && !error && <p className="muted">Loading…</p>}
      {data && !shown.length && <p className="muted">No tickets here.</p>}
      {shown.length > 0 && (
        <div className="table-scroll">
          <table className="table clickable">
            <thead>
              <tr><th>Ticket</th><th>Raised by</th><th>Status</th><th>With</th><th>Last activity</th></tr>
            </thead>
            <tbody>
              {shown.map((t) => (
                <tr key={t.id} tabIndex={0} onClick={() => navigate(`/helpdesk/${t.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/helpdesk/${t.id}`); }}>
                  <td>
                    <strong>{t.subject}</strong>
                    <div className="small muted">{t.reference} · {TICKET_CATEGORY_LABEL[t.category]}</div>
                  </td>
                  <td>{t.raisedByName}<div className="small muted">{formatDateTime(t.raisedAt)}</div></td>
                  <td>
                    <TicketStatus status={t.status} />
                    {(t.priority === 'high' || t.priority === 'urgent') && <div><span className="status status-aborted">{TICKET_PRIORITY_LABEL[t.priority]}</span></div>}
                  </td>
                  <td>{t.assignedToId === me.id ? 'Me' : t.assignedToName || <span className="muted">Nobody</span>}</td>
                  <td>
                    {formatDateTime(t.lastMessageAt)}
                    {TICKET_OPEN.includes(t.status) && t.lastSide === 'raiser' && t.status !== 'waiting_on_user' && <div><span className="status status-on_the_way">Needs reply</span></div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
